import { Action, isAnyOf } from '@reduxjs/toolkit';
import { isEmpty } from 'ramda';

import { leaveBreadcrumb } from 'cfa-react-logging';

import ooeConstants from '../constants';
import { DashboardOrder } from '../dashboard/schemas';
import { AppStartListening } from '../listenerMiddleware';
import { actions as orderActions } from '../order/reducers';
import { selectOverrideGuestDetails } from '../order/selectors';
import { submitOrder } from '../order/thunks';
import { State } from '../reducers';
import { logEvent } from '../services/analytics';
import { actions as userActions } from '../user/reducer';
import { selectUserHasAgreedToEula } from '../user/selectors';
import { addUserToAcknowledgement, checkEulaMessageOnLoad } from '../user/thunks';
import { getPaymentAccountById } from './apis';
import { actions as guestActions } from './reducer';
import {
  selectCmtCustomerCFAId,
  selectCmtCustomerIsRegisteredMember,
  selectFirstName,
  selectGuestValues,
  selectLastName,
  selectMemberSearchValue,
  selectPastOrdersLength,
} from './selectors';
import {
  addToFavorites,
  getFavoriteOrders,
  getPastDeliveryAddresses,
  getPastOrders,
  getVaultedCards,
  initializeGuestAccount,
  removeFromFavorites,
  searchForMembers,
  searchGuestByPhone,
  searchGuestsByEmail,
  searchGuestsByName,
  updateFavoriteName,
  validateCardZipcode,
} from './thunks';
import { isCustomerAccount } from './utils';

const addGuestListeners = (startListening: AppStartListening) => {
  // Persists across effect runs (unlike cancelActiveListeners, which only reaches a
  // pending debounce timer, not a request already dispatched by a previous run) so a
  // superseded first-page search can have its rex-catering call cancelled outright.
  let activeMemberSearch: { abort: (reason?: string) => void } | undefined;
  // Same idea for edit-order ACCOUNT restore: cancelActiveListeners only reaches take(),
  // not an in-flight getPaymentAccountById. Abort the prior controller so a stale response
  // cannot dispatch getOrderAccountSuccess after a newer edit/exit.
  let activePaymentAccountRestore: AbortController | undefined;

  startListening({
    actionCreator: orderActions.exitEditOrder,
    effect: (_, listenerApi) => {
      activePaymentAccountRestore?.abort('Edit order exited');
      activePaymentAccountRestore = undefined;
      const { dispatch } = listenerApi;
      dispatch(guestActions.resetState());
    },
  });

  startListening({
    actionCreator: guestActions.setGuestFormField,
    effect: (action, listenerApi) => {
      const { field } = action.payload;
      const { getState, dispatch } = listenerApi;
      const state = getState() as State;

      if ((field === 'first' || field === 'last') && selectOverrideGuestDetails(state)) {
        const first = selectFirstName(state);
        const last = selectLastName(state);
        dispatch(orderActions.setEditOrderDismissibleMessage(`${first} ${last}`));
      }
    },
  });

  startListening({
    actionCreator: orderActions.setPaymentMethod,
    effect: (_, { dispatch }) => {
      dispatch(guestActions.clearValidateZipError());
    },
  });

  startListening({
    actionCreator: orderActions.initiateEditOrder,
    effect: (action, listenerApi) => {
      const { reorder, order } = action.payload;

      if (reorder) {
        return;
      }

      const guest = action.payload.guest as GuestDetails;
      const { accountType } = order as DashboardOrder;
      const cmtCustomer = {
        ...guest,
        firstName: guest.first ?? '',
        lastName: guest.last ?? '',
        first: undefined,
        last: undefined,
        isMember: isCustomerAccount({ accountType }),
      };

      const { dispatch } = listenerApi;
      dispatch(guestActions.setCMTCustomer(cmtCustomer));
    },
  });

  startListening({
    matcher: isAnyOf(
      searchGuestsByName.fulfilled,
      searchGuestsByEmail.fulfilled,
      searchGuestByPhone.fulfilled,
      validateCardZipcode.fulfilled,
      guestActions.setCMTCustomer,
      addToFavorites.pending,
      removeFromFavorites.pending,
      updateFavoriteName.pending,
      getPastDeliveryAddresses.pending,
    ),
    effect: (action) => {
      // we don't send the payload for guest events so as to protect sensitive data
      const { type } = action;
      logEvent(type);
    },
  });

  startListening({
    actionCreator: guestActions.queueSearchGuestsByName,
    effect: async (action, listenerApi) => {
      const { dispatch, cancelActiveListeners, delay, extra } = listenerApi;

      // debounce (https://redux-toolkit.js.org/api/createListenerMiddleware#complex-async-workflows)
      cancelActiveListeners();
      await delay(ooeConstants.GUEST_SEARCH_DELAY_TIME);

      if (extra.testUtils?.debounceCallback) {
        extra.testUtils.debounceCallback(action);
      }

      dispatch(searchGuestsByName());
    },
  });

  startListening({
    actionCreator: guestActions.queueSearchGuestsByEmail,
    effect: async (action, listenerApi) => {
      const { dispatch, cancelActiveListeners, delay, extra } = listenerApi;

      // debounce (https://redux-toolkit.js.org/api/createListenerMiddleware#complex-async-workflows)
      cancelActiveListeners();
      await delay(ooeConstants.GUEST_SEARCH_DELAY_TIME);

      if (extra.testUtils?.debounceCallback) {
        extra.testUtils.debounceCallback(action);
      }

      dispatch(searchGuestsByEmail());
    },
  });

  startListening({
    predicate: (action: Action): action is ReturnType<typeof guestActions.setCMTCustomer> =>
      guestActions.setCMTCustomer.match(action) && !!action.payload.cfaId,
    effect: async (action, listenerApi) => {
      const { getState, dispatch } = listenerApi;
      const cmtCustomer = action.payload;
      dispatch(guestActions.retrieveGuestAdditionalInformation());
      const state = getState() as State;
      if (selectOverrideGuestDetails(state)) {
        const first = cmtCustomer.first ?? cmtCustomer.firstName ?? '';
        const last = cmtCustomer.last ?? cmtCustomer.lastName ?? '';
        dispatch(orderActions.setEditOrderDismissibleMessage(`${first} ${last}`));
      }
    },
  });

  startListening({
    predicate: (action: Action) => {
      if (
        isAnyOf(
          guestActions.resetState,
          orderActions.exitEditOrder,
          userActions.updateUserInfo,
          checkEulaMessageOnLoad.fulfilled,
          addUserToAcknowledgement.fulfilled,
        )(action)
      ) {
        return true;
      }
      return guestActions.setCMTCustomer.match(action) && !action.payload.cfaId;
    },
    effect: async (_, listenerApi) => {
      const { getState } = listenerApi;
      const state = getState() as State;

      const hasAgreedToEula = selectUserHasAgreedToEula(state);
      if (!hasAgreedToEula) {
        return;
      }

      leaveBreadcrumb('Session started');

      const { dispatch } = listenerApi;
      await dispatch(initializeGuestAccount());
    },
  });

  startListening({
    actionCreator: guestActions.queueSearchGuestsByPhone,
    effect: async (action, listenerApi) => {
      const { dispatch, cancelActiveListeners, delay, extra } = listenerApi;

      // debounce (https://redux-toolkit.js.org/api/createListenerMiddleware#complex-async-workflows)
      cancelActiveListeners();
      await delay(ooeConstants.GUEST_SEARCH_DELAY_TIME);

      if (extra.testUtils?.debounceCallback) {
        extra.testUtils.debounceCallback(action);
      }

      dispatch(searchGuestByPhone());
    },
  });

  startListening({
    matcher: isAnyOf(guestActions.setMemberSearchValue, guestActions.submitMemberSearch),
    effect: async (action, listenerApi) => {
      const { cancelActiveListeners, delay, dispatch, extra, getState } = listenerApi;

      // debounce (https://redux-toolkit.js.org/api/createListenerMiddleware#complex-async-workflows)
      // Enter shares this listener so it cancels the keystroke timer -- cancelActiveListeners
      // only reaches other runs of the same listener entry.
      cancelActiveListeners();

      const isSubmit = guestActions.submitMemberSearch.match(action);
      const query = selectMemberSearchValue(getState() as State).trim();
      const minLength = isSubmit
        ? ooeConstants.MEMBER_SEARCH_MIN_SUBMIT_LENGTH
        : ooeConstants.MEMBER_SEARCH_MIN_TYPEAHEAD_LENGTH;

      if (query.length < minLength) {
        activeMemberSearch?.abort('Superseded by a newer member search');
        activeMemberSearch = undefined;
        return;
      }

      if (!isSubmit) {
        await delay(ooeConstants.MEMBER_SEARCH_DEBOUNCE_TIME);
      }

      if (extra.testUtils?.debounceCallback) {
        extra.testUtils.debounceCallback(action);
      }

      activeMemberSearch?.abort('Superseded by a newer member search');
      activeMemberSearch = dispatch(searchForMembers());
    },
  });

  startListening({
    actionCreator: guestActions.retrieveGuestAdditionalInformation,
    effect: async (_, listenerApi) => {
      const { dispatch } = listenerApi;
      dispatch(getVaultedCards());
    },
  });

  startListening({
    actionCreator: orderActions.initiateEditOrder,
    effect: async (action, listenerApi) => {
      const {
        reorder,
        guest,
        order: { payment },
      } = action.payload;
      if (
        reorder ||
        !payment ||
        !payment.accountId ||
        payment.paymentType !== ooeConstants.GET_PAYMENT_TYPES.ACCOUNT
      ) {
        return;
      }

      const { dispatch, take, getState, cancelActiveListeners } = listenerApi;
      // Cancel a prior edit's take() wait; AbortController covers getPaymentAccountById below.
      cancelActiveListeners();
      activePaymentAccountRestore?.abort('Superseded by a newer edit order');
      const paymentAccountRestore = new AbortController();
      activePaymentAccountRestore = paymentAccountRestore;

      const guestCfaId = (guest as GuestDetails).cfaId;
      if (guestCfaId) {
        // Vaulted cards are only requested when edit retrieves guest extras (cfaId present).
        const [vaultedCardsResult] = await take(
          isAnyOf(
            getVaultedCards.fulfilled,
            getVaultedCards.rejected,
            orderActions.exitEditOrder,
            orderActions.initiateEditOrder,
          ),
        );
        if (
          getVaultedCards.rejected.match(vaultedCardsResult) ||
          orderActions.exitEditOrder.match(vaultedCardsResult) ||
          orderActions.initiateEditOrder.match(vaultedCardsResult)
        ) {
          if (activePaymentAccountRestore === paymentAccountRestore) {
            activePaymentAccountRestore = undefined;
          }
          return;
        }
      }

      const state = getState() as State;
      const cfaId = selectCmtCustomerCFAId(state);
      const isRegisteredMember = selectCmtCustomerIsRegisteredMember(state);

      const { accountId } = payment;
      const account = await getPaymentAccountById({
        accountId,
        cfaId,
        isRegisteredMember,
        signal: paymentAccountRestore.signal,
      }).catch(() => undefined);

      if (
        paymentAccountRestore.signal.aborted ||
        activePaymentAccountRestore !== paymentAccountRestore ||
        !account ||
        isEmpty(account)
      ) {
        return;
      }

      dispatch(guestActions.getOrderAccountSuccess({ account }));
      if (activePaymentAccountRestore === paymentAccountRestore) {
        activePaymentAccountRestore = undefined;
      }
    },
  });

  startListening({
    actionCreator: guestActions.retrieveGuestAdditionalInformation,
    effect: (_, { dispatch }) => {
      dispatch(getPastOrders({ numToLoad: ooeConstants.NUM_ADDTL_PAST_ORDERS_TO_LOAD }));
    },
  });

  startListening({
    actionCreator: guestActions.loadMorePastOrders,
    effect: (_, { dispatch, getState }) => {
      const state = getState() as State;
      const numOfPastOrders = selectPastOrdersLength(state);
      dispatch(
        getPastOrders({
          numToLoad: numOfPastOrders + ooeConstants.NUM_ADDTL_PAST_ORDERS_TO_LOAD,
        }),
      );
    },
  });

  startListening({
    matcher: isAnyOf(
      guestActions.retrieveGuestAdditionalInformation,
      addToFavorites.fulfilled,
      removeFromFavorites.fulfilled,
      updateFavoriteName.fulfilled,
    ),
    effect: (_, { dispatch }) => {
      dispatch(getFavoriteOrders());
    },
  });

  startListening({
    actionCreator: guestActions.overrideGuestDetails,
    effect: (_, { dispatch, getState }) => {
      logEvent(ooeConstants.AMPLITUDE_EVENT_NAMES.OVERRIDE_GUEST_DETAILS);
      leaveBreadcrumb('Override Guest Details', { message: 'User confirmed override of guest details' });
      dispatch(orderActions.setOverrideGuestDetails(true));
      dispatch(orderActions.setSmsConsent({ smsConsent: null }));
      const { first, last, email, phone } = selectGuestValues(getState() as State);
      dispatch(
        guestActions.setCMTCustomer({
          cfaId: '',
          isMember: false,
          first,
          last,
          email,
          phone,
        }),
      );
    },
  });

  startListening({
    actionCreator: submitOrder.fulfilled,
    effect: (_, { dispatch }) => {
      dispatch(guestActions.resetState());
    },
  });
};

export default addGuestListeners;
